import { useRef, useState } from "react";
import { FolderDown, Loader2, Plus, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { fileUrl } from "@/lib/api";
import { uploadMedia } from "@/lib/cloudApi";
import { importAdminDriveFolderMedia } from "@/lib/galleryApi";
import { getSupabaseConfigurationError, supabase } from "@/lib/supabase";
import {
  compressImageFile,
  getUploadErrorMessage,
  normalizeAndValidateExternalImage,
  validateImageFile,
} from "@/lib/adminImageUtils";

export default function MultiImagePicker({
  urls = [],
  onChange,
  label = "Gallery",
  testId = "multi-picker",
  folder = "uploads/gallery",
  onUploadingChange,
  allowDriveFolderImport = false,
}) {
  const [busy, setBusy] = useState(false);
  const [folderBusy, setFolderBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [urlInput, setUrlInput] = useState("");
  const [folderInput, setFolderInput] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  const upload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setError("");
    setBusy(true);
    onUploadingChange?.(true);
    setProgress({ done: 0, total: files.length });
    const uploaded = [];

    try {
      for (let i = 0; i < files.length; i += 1) {
        const raw = files[i];
        validateImageFile(raw);
        const file = await compressImageFile(raw, { maxEdge: 2000, quality: 0.84 });
        const res = await uploadMedia(file, folder, (filePct) => {
          setProgress({ done: i + filePct / 100, total: files.length });
        });
        uploaded.push(res.url || res);
        setProgress({ done: i + 1, total: files.length });
      }
      onChange([...urls, ...uploaded]);
      toast.success(`${uploaded.length} photo${uploaded.length !== 1 ? "s" : ""} uploaded`);
    } catch (err) {
      const message = getUploadErrorMessage(err);
      console.error("[ADMIN_MULTI_IMAGE_UPLOAD_ERROR]", { folder, message, error: err });
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
      onUploadingChange?.(false);
      setProgress({ done: 0, total: 0 });
      event.target.value = "";
    }
  };

  const addUrl = async () => {
    setError("");
    try {
      const url = await normalizeAndValidateExternalImage(urlInput);
      onChange([...urls, url]);
      setUrlInput("");
    } catch (err) {
      console.error("[ADMIN_MULTI_IMAGE_URL_ERROR]", { input: urlInput, message: err.message });
      setError(err.message);
      toast.error(err.message);
    }
  };

  const importDriveFolder = async () => {
    setError("");
    if (!folderInput.trim()) {
      const message = "Paste a Google Drive folder link or folder ID.";
      setError(message);
      toast.error(message);
      return;
    }
    if (!supabase) {
      const message = getSupabaseConfigurationError() || "Supabase is not configured.";
      setError(message);
      toast.error(message);
      return;
    }

    setFolderBusy(true);
    onUploadingChange?.(true);
    try {
      const { data, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      const accessToken = data?.session?.access_token;
      if (!accessToken) throw new Error("Admin login session is required to import a Drive folder.");

      const result = await importAdminDriveFolderMedia(folderInput, accessToken);
      const imageUrls = (result.media || [])
        .filter((item) => item.mediaType === "image")
        .map((item) => item.previewUrl || item.thumbnailUrl)
        .filter(Boolean);
      if (!imageUrls.length) {
        throw new Error("No image files were found in this Google Drive folder.");
      }

      const merged = [...urls];
      const seen = new Set(merged);
      imageUrls.forEach((url) => {
        if (!seen.has(url)) {
          seen.add(url);
          merged.push(url);
        }
      });
      onChange(merged);
      setFolderInput("");

      const skippedVideos = Number(result.importedVideos || 0);
      toast.success(
        skippedVideos
          ? `Imported ${imageUrls.length} photos. ${skippedVideos} videos skipped for this image gallery.`
          : `Imported ${imageUrls.length} photos from Google Drive`
      );
    } catch (err) {
      const message = err.message || "Google Drive folder import failed.";
      console.error("[ADMIN_DRIVE_FOLDER_IMPORT_ERROR]", { message });
      setError(message);
      toast.error(message);
    } finally {
      setFolderBusy(false);
      onUploadingChange?.(false);
    }
  };

  const removeAt = (index) => onChange(urls.filter((_, itemIndex) => itemIndex !== index));

  return (
    <div data-testid={testId} className="space-y-3">
      <label className="eyebrow text-[10px] text-[#C99A45]/80 block">
        {label} ({urls.length} photo{urls.length !== 1 ? "s" : ""})
      </label>

      <div className="flex gap-2 flex-wrap">
        <input
          value={urlInput}
          onChange={(event) => setUrlInput(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && (event.preventDefault(), addUrl())}
          placeholder="Paste direct image URL or Google Drive image file link..."
          className="admin-input flex-1 min-w-0 !py-2 text-sm"
          data-testid={`${testId}-url-input`}
        />
        <button
          type="button"
          onClick={addUrl}
          className="btn-outline-gold !py-2 !px-3 text-xs shrink-0"
          data-testid={`${testId}-add-url`}
        >
          <Plus size={12} /> URL
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          multiple
          onChange={upload}
          className="hidden"
          data-testid={`${testId}-file-input`}
        />
        <button
          type="button"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          className="btn-gold !py-2 !px-3 text-xs shrink-0 disabled:opacity-60"
          data-testid={`${testId}-upload`}
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
          {busy ? `${pct}%` : "Upload"}
        </button>
      </div>

      {allowDriveFolderImport && (
        <div className="rounded-2xl border border-[#C99A45]/20 bg-[#C99A45]/5 p-3 space-y-2">
          <div>
            <p className="eyebrow text-[9px] text-[#C99A45]/80">Import Google Drive Folder</p>
            <p className="text-[11px] text-[#6f6a62]">
              Folder links belong here. The URL field above is only for single image file links.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <input
              value={folderInput}
              onChange={(event) => setFolderInput(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && (event.preventDefault(), importDriveFolder())}
              placeholder="Paste Google Drive folder link or folder ID"
              className="admin-input flex-1 min-w-0 !py-2 text-sm"
              data-testid={`${testId}-folder-input`}
            />
            <button
              type="button"
              disabled={folderBusy}
              onClick={importDriveFolder}
              className="btn-outline-gold !py-2 !px-3 text-xs shrink-0 disabled:opacity-60"
              data-testid={`${testId}-import-folder`}
            >
              {folderBusy ? <Loader2 size={12} className="animate-spin" /> : <FolderDown size={12} />}
              {folderBusy ? "Importing..." : "Import Folder"}
            </button>
          </div>
        </div>
      )}

      {busy && progress.total > 0 && (
        <div className="space-y-1">
          <div className="w-full h-1.5 bg-[#ece7dd] rounded-full overflow-hidden">
            <div className="h-full bg-[#C99A45] transition-all" style={{ width: `${pct}%` }} />
          </div>
          <p className="eyebrow text-[9px] text-[#6f6a62]">
            Compressing &amp; uploading {Math.min(progress.total, Math.ceil(progress.done))}/{progress.total}...
          </p>
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-500/25 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      )}

      {urls.length > 0 && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          {urls.map((url, index) => (
            <div key={`${url}-${index}`} className="relative group">
              <img
                src={fileUrl(url)}
                alt=""
                loading="lazy"
                decoding="async"
                className="w-full aspect-square object-cover border border-[#C99A45]/20 bg-[#1a1a1a]"
              />
              <button
                type="button"
                onClick={() => removeAt(index)}
                className="absolute top-1 right-1 w-5 h-5 bg-[#E52717] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                data-testid={`${testId}-remove-${index}`}
                aria-label={`Remove photo ${index + 1}`}
              >
                <X size={10} />
              </button>
              <div className="absolute bottom-0 left-0 right-0 bg-black/40 text-white text-[8px] text-center py-0.5 opacity-0 group-hover:opacity-100 transition">
                {index + 1}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
